import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, CircularProgress, Alert, Autocomplete, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ClientService from '../../../Services/client.service';
import ImportExportService from '../../../Services/importExport.service';
import EnumsService from '../../../Services/enums.service';
import CustomMonthYearIndividualDatePicker from '../CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import BmkvdPreviewModal from '../../ClientDetails/SubComponents/ClientDetailsHeader/BmkvdPreviewModal';

// Export only. Importing a uniform file is its own top-level modal, opened straight from the
// journal import menu, so the flow never stacks three modals deep.
function DownloadUniformFile({
  client,
  token,
  open,
  onClose,
  direction = 'rtl',
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [sortCodes, setSortCodes] = useState([]);
  const [receiptSortCodes, setReceiptSortCodes] = useState([]);
  const [inventorySortCodes, setInventorySortCodes] = useState([]);

  // Same toggles as Ledger Report: registration month vs document date, and processed-only.
  const [filterByDocumentDate, setFilterByDocumentDate] = useState(() => {
    const stored = localStorage.getItem('bmkvdExportFilterByDocumentDate');
    return stored === 'true';
  });
  const [onlyProcessed, setOnlyProcessed] = useState(() => {
    const stored = localStorage.getItem('bmkvdExportOnlyProcessed');
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    if (!open || !client?.accountantId || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setSortCodes(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching sort codes:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, client?.accountantId, token]);

  const exportOptions = () => ({
    receiptSortCodeIds: receiptSortCodes.map((sc) => sc.sortCodeId),
    inventorySortCodeIds: inventorySortCodes.map((sc) => sc.sortCodeId),
    filterByDocumentDate,
    onlyProcessed,
  });

  const handlePreview = async () => {
    if (!client?.caseId) {
      console.warn('No client or caseId found');
      return;
    }

    if (!startDate || !endDate) {
      alert(t('InvalidDateSelection'));
      return;
    }

    setLoading(true);
    setPreviewError(null);

    try {
      const options = exportOptions();
      const preview = await ImportExportService.PreviewBmkvdExport(
        client.caseId,
        startDate.toISOString(),
        endDate.toISOString(),
        token,
        options.receiptSortCodeIds,
        options.inventorySortCodeIds,
        options.filterByDocumentDate,
        options.onlyProcessed
      );

      setPreviewData(preview);
      setPreviewError(null);
      setShowPreview(true);
    } catch (error) {
      console.error('Error previewing BMKVD export:', error);
      const displayMessage = error?.message || t('BmkvdPreviewFailed');
      setPreviewData(null);
      setPreviewError(displayMessage);
      setShowPreview(true);
    }

    setLoading(false);
  };

  const handleDownload = async () => {
    if (!client?.caseId) {
      console.warn('No client or caseId found');
      return;
    }

    setDownloading(true);

    try {
      if (!startDate || !endDate) {
        alert(t('InvalidDateSelection'));
        setDownloading(false);
        return;
      }

      const options = exportOptions();
      await ImportExportService.DownloadBmkvdExport(
        client.caseId,
        startDate.toISOString(),
        endDate.toISOString(),
        token,
        options.receiptSortCodeIds,
        options.inventorySortCodeIds,
        options.filterByDocumentDate,
        options.onlyProcessed
      );

      setShowPreview(false);
    } catch (error) {
      console.error(error);
      alert('Error downloading uniform file: ' + error.message);
    }

    setDownloading(false);
  };

  const handleStartDateChangeNew = (newValue, type) => {
    const newDate = new Date(newValue);
    let updatedDate;

    if (type === 'year') {
      updatedDate = new Date(Date.UTC(newDate.getUTCFullYear(), startDate.getUTCMonth(), 10));
    } else if (type === 'month') {
      updatedDate = new Date(Date.UTC(startDate.getUTCFullYear(), newDate.getUTCMonth(), 10));
    }

    localStorage.setItem('StartDate', updatedDate.toISOString());
    setStartDate(updatedDate);
  };

  const handleEndDateChangeNew = (newValue, type) => {
    const newDate = new Date(newValue);
    let updatedDate;

    if (type === 'year') {
      updatedDate = new Date(Date.UTC(newDate.getUTCFullYear(), endDate.getUTCMonth(), 10));
    } else if (type === 'month') {
      updatedDate = new Date(Date.UTC(endDate.getUTCFullYear(), newDate.getUTCMonth(), 10));
    }

    localStorage.setItem('EndDate', updatedDate.toISOString());
    setEndDate(updatedDate);
  };

  useEffect(() => {
    const savedStart = localStorage.getItem('StartDate');
    const savedEnd = localStorage.getItem('EndDate');
    if (savedStart) setStartDate(new Date(savedStart));
    if (savedEnd) setEndDate(new Date(savedEnd));
  }, []);

  const handleDateBasisChange = (byDocumentDate) => {
    setFilterByDocumentDate(byDocumentDate);
    localStorage.setItem('bmkvdExportFilterByDocumentDate', String(byDocumentDate));
  };

  const handleOnlyProcessedChange = (checkedShowUnprocessed) => {
    const nextOnlyProcessed = !checkedShowUnprocessed;
    setOnlyProcessed(nextOnlyProcessed);
    localStorage.setItem('bmkvdExportOnlyProcessed', String(nextOnlyProcessed));
  };

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="uniform-file-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 360,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <h2 id="uniform-file-modal" style={{ margin: 0 }}>
            {t('UniformFileManagement')}
          </h2>

          <p style={{ margin: 0 }}>{t('SelectStartAndEndPeriod')}</p>

          <CustomMonthYearIndividualDatePicker
            year={startDate}
            month={startDate}
            onYearChange={(val) => handleStartDateChangeNew(val, 'year')}
            onMonthChange={(val) => handleStartDateChangeNew(val, 'month')}
            labelYear={t('StartYear')}
            labelMonth={t('StartMonth')}
            height="48px"
            widthOuter="270px"
            widthInner="120px"
            flip={true}
          />

          <CustomMonthYearIndividualDatePicker
            year={endDate}
            month={endDate}
            onYearChange={(val) => handleEndDateChangeNew(val, 'year')}
            onMonthChange={(val) => handleEndDateChangeNew(val, 'month')}
            labelYear={t('EndYear')}
            labelMonth={t('EndMonth')}
            height="48px"
            widthOuter="270px"
            widthInner="120px"
            flip={true}
          />

          <Box
            sx={{
              display: 'flex',
              border: '1px solid #DEE2E6',
              borderRadius: '4px',
              overflow: 'hidden',
              fontSize: '12px',
              width: '100%',
              maxWidth: 270,
            }}
          >
            <Box
              onClick={() => handleDateBasisChange(true)}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.75,
                cursor: 'pointer',
                backgroundColor: filterByDocumentDate ? '#304FFF' : 'transparent',
                color: filterByDocumentDate ? '#FFFFFF' : '#6C757D',
                fontWeight: filterByDocumentDate ? 600 : 500,
              }}
            >
              {t('DocumentDate')}
            </Box>
            <Box
              onClick={() => handleDateBasisChange(false)}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.75,
                cursor: 'pointer',
                backgroundColor: !filterByDocumentDate ? '#304FFF' : 'transparent',
                color: !filterByDocumentDate ? '#FFFFFF' : '#6C757D',
                fontWeight: !filterByDocumentDate ? 600 : 500,
              }}
            >
              {t('RegistrationMonth')}
            </Box>
          </Box>

          <FormControlLabel
            sx={{ alignSelf: 'stretch', m: 0 }}
            control={
              <Checkbox
                checked={!onlyProcessed}
                onChange={(e) => handleOnlyProcessedChange(e.target.checked)}
              />
            }
            label={t('ShowUnprocessedDocuments')}
          />

          <Autocomplete
            multiple
            fullWidth
            options={sortCodes}
            value={receiptSortCodes}
            onChange={(e, newValue) => setReceiptSortCodes(newValue)}
            getOptionLabel={(option) =>
              `${option.sortCodeId ?? ''} - ${option.sortCodeName ?? ''}`.trim()
            }
            isOptionEqualToValue={(option, val) => option.sortCodeId === val.sortCodeId}
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField {...params} label={t('ReceiptSortCodes')} placeholder={t('SelectSortCodes')} />
            )}
          />

          <Autocomplete
            multiple
            fullWidth
            options={sortCodes}
            value={inventorySortCodes}
            onChange={(e, newValue) => setInventorySortCodes(newValue)}
            getOptionLabel={(option) =>
              `${option.sortCodeId ?? ''} - ${option.sortCodeName ?? ''}`.trim()
            }
            isOptionEqualToValue={(option, val) => option.sortCodeId === val.sortCodeId}
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField {...params} label={t('InventorySortCodes')} placeholder={t('SelectSortCodes')} />
            )}
          />

          {loading && <CircularProgress />}

          {!loading && previewError && (
            <Alert severity="error" sx={{ width: '100%', alignSelf: 'stretch' }}>
              {previewError}
            </Alert>
          )}

          {!loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
              <Button onClick={onClose}>{t('Cancel')}</Button>
              <Button variant="outlined" onClick={handlePreview}>
                {t('Preview')}
              </Button>
              <Button variant="contained" onClick={handleDownload} disabled={downloading}>
                {downloading ? <CircularProgress size={20} /> : t('Download')}
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      <BmkvdPreviewModal
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewError(null);
        }}
        previewData={previewData}
        loading={loading}
        error={previewError}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </>
  );
}

export default DownloadUniformFile;
